import { useMutation, useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_ROLES } from '~/gql/role/role'
import { GQL_ADD_USER, GQL_UPDATE_USER } from '~/gql/users/users'
import { client } from '~/helpers/apollo'
import { useAuth } from '~/helpers/store/auth'

/**
 * Valeur sentinelle du choix « aucun groupe » : Radix Select refuse un
 * `SelectItem` de valeur vide, qu'il réserve à l'état non renseigné.
 */
const NO_GROUP = '__none__'

type Props = {
  open: boolean
  defaultValues?: {
    id?: string
    email?: string
    firstName?: string
    lastName?: string
    roleId?: string
  }
  onCancel: () => void
  onSuccess: () => void
}

type FormData = {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  roleId?: string
  /**
   * Nom du groupe, et non son id : c'est le champ que porte `CreateUserInput`,
   * partagé tel quel avec l'import CSV (#135). Vide = aucun rattachement.
   */
  group?: string
}

function UserModal({ open, onCancel, onSuccess, defaultValues }: Props) {
  const { data } = useQuery(GQL_ROLES)
  // Les groupes ne concernent que la création : à l'édition, l'appartenance se
  // gère depuis la page Groupes ou l'affectation en masse (elle est N-N).
  const { data: groupsData } = useQuery(GQL_GROUPS, { skip: !!defaultValues?.id })

  const { t } = useTranslation()
  const { payload } = useAuth()

  const { register, handleSubmit, setValue, getValues, reset, formState } = useForm<FormData>({
    defaultValues: {
      id: defaultValues?.id || '',
      email: defaultValues?.email || '',
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      roleId: defaultValues?.roleId || '',
      group: '',
    },
  })

  const isUpdate = !!getValues('id')

  const [updateUser] = useMutation(GQL_UPDATE_USER, {
    client,
  })

  // `createUser` provisionne l'invitation avec les vraies valeurs saisies et le
  // rôle choisi, là où l'ancien `askForRegister` écrivait « Unknown » en dur et
  // ignorait le rôle (#148). C'est le même chemin que l'import CSV.
  const [addUser] = useMutation(GQL_ADD_USER, {
    client,
  })

  const onSubmit = async () => {
    // Si c'est une mise à jour d'utilisateur
    if (isUpdate) {
      try {
        await updateUser({
          variables: {
            updateUserInput: {
              id: getValues('id') || '',
              email: getValues('email') || '',
              firstName: getValues('firstName') || '',
              lastName: getValues('lastName') || '',
              roleId: getValues('roleId') || '',
            },
          },
        })
        toast.success(t('good.update.user'))
        onSuccess()
        onCancel()
        reset()
      } catch {
        toast.error(t('bad.update.user'))
      }
      return
    }

    try {
      await addUser({
        variables: {
          createUserInput: {
            email: getValues('email') || '',
            firstName: getValues('firstName') || '',
            lastName: getValues('lastName') || '',
            roleId: getValues('roleId') || '',
            // Champ optionnel : omis plutôt que vide, pour ne pas demander au
            // serveur de résoudre un groupe au libellé blanc.
            group: getValues('group') || undefined,
          },
        },
      })
      toast.success(t('good.add.user'))
      onSuccess()
      onCancel()
      reset()
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      toast.error(t(message.includes('EMAIL_ALREADY_USED') ? 'add.user.emailUsed' : 'bad.add.user'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isUpdate ? t('update.user') : t('create.user')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('common.email')}</Label>
            <Input
              id="email"
              type="email"
              {...register('email', {
                onChange: (e) => {
                  setValue('email', e.target.value)
                },
                required: t('input.required.message'),
                pattern: {
                  value: /^[\w\-\\.]+@([\w\-]+\.)+[\w\-]{2,4}$/g,
                  message: t('input.email.message'),
                },
              })}
            />
            {formState.errors.email && (
              <p className="text-sm text-red-500">
                {formState.errors.email.message}
              </p>
            )}
          </div>

          {/*
            Prénom et nom sont requis dans les deux modes : à la création, les
            omettre laissait le compte à « Unknown » jusqu'à une édition
            manuelle, alors que l'import CSV les exige déjà (#148).
          */}
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('common.lastname')}</Label>
            <Input
              id="lastName"
              type="text"
              {...register('lastName', {
                onChange: (e) => {
                  setValue('lastName', e.target.value)
                },
                required: t('input.required.message'),
              })}
            />
            {formState.errors.lastName && (
              <p className="text-sm text-red-500">
                {formState.errors.lastName.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="firstName">{t('common.firstname')}</Label>
            <Input
              id="firstName"
              type="text"
              {...register('firstName', {
                onChange: (e) => {
                  setValue('firstName', e.target.value)
                },
                required: t('input.required.message'),
              })}
            />
            {formState.errors.firstName && (
              <p className="text-sm text-red-500">
                {formState.errors.firstName.message}
              </p>
            )}
          </div>

          {/*
            Rattachement au groupe, proposé à la création seulement — et sur les
            deux parcours à la fois (import CSV compris), les traiter séparément
            étant exactement ce qui a produit #148.
          */}
          {!isUpdate && (
            <div className="space-y-2">
              <Label htmlFor="group">{t('common.group')}</Label>
              <Select
                value={getValues('group')}
                onValueChange={(value: string) => {
                  setValue('group', value === NO_GROUP ? '' : value)
                }}
              >
                <SelectTrigger id="group">
                  <SelectValue placeholder={t('groups.none')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_GROUP}>{t('groups.none')}</SelectItem>
                  {groupsData?.groups.map(group => (
                    <SelectItem key={group.id} value={group.name}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {
            payload && payload.role === RoleEnum.Admin && (
              <div className="space-y-2">
                <Label htmlFor="role">{t('common.role')}</Label>
                <Select
                  value={getValues('roleId')}
                  onValueChange={(value: string) => {
                    setValue('roleId', value, { shouldValidate: true })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {data?.getRoles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )
          }

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel} type="button">
              {t('common.cancel')}
            </Button>
            <Button type="submit">{isUpdate ? t('common.update') : t('common.create')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default UserModal
